import { useMemo } from "react";

import { keyBy, omit } from "lodash";

import { usePerformanceTrackingTableSelectionState } from "../../hooks/use-selection-state";
import {
  type LoadingRowType,
  type PerformanceTrackingTableRow,
  type TableRowType,
} from "../components/performance-tracking-table";

function isParent(
  potentialParent: Record<string, string>,
  potentialChild: Record<string, string>,
) {
  return (
    Object.keys(potentialParent).every(
      (key) => potentialParent[key] === potentialChild[key],
    ) &&
    Object.keys(potentialChild).length > Object.keys(potentialParent).length
  );
}

export function useSelectedRows(rows: PerformanceTrackingTableRow[]) {
  const [selectionState] = usePerformanceTrackingTableSelectionState();

  const result = useMemo(() => {
    const flattenedRows = flattenRows(rows);

    const deselectionSet = new Set(selectionState.deselectedRowIds);
    const selectionSet = new Set(selectionState.selectedRowIds);

    const rowsById = keyBy(flattenedRows, "key");

    const filtered = flattenedRows.filter((row) => {
      if (row.rowType === "loading") {
        return true;
      }
      if (selectionSet.has(row.key)) {
        return true;
      }
      if (deselectionSet.has(row.key)) {
        return false;
      }

      let closestParent: Omit<TableRowType, "children"> | null = null;
      let isSelected = false;
      selectionState?.selectedRowIds?.forEach((rowId) => {
        const potentialParent = rowsById[rowId];
        if (!potentialParent) {
          return;
        }
        if (potentialParent.rowType === "loading") {
          return;
        }
        const isParentOfRow = isParent(
          potentialParent.metadata.breakdowns,
          row.metadata.breakdowns,
        );

        if (
          isParentOfRow &&
          (!closestParent ||
            Object.keys(closestParent.metadata.breakdowns).length <
              Object.keys(potentialParent.metadata.breakdowns).length)
        ) {
          closestParent = potentialParent;
          isSelected = true;
        }
      });

      selectionState?.deselectedRowIds?.forEach((rowId) => {
        const potentialParent = rowsById[rowId];
        if (!potentialParent) {
          return;
        }
        if (potentialParent.rowType === "loading") {
          return;
        }
        const isParentOfRow = isParent(
          potentialParent.metadata.breakdowns,
          row.metadata.breakdowns,
        );

        if (
          isParentOfRow &&
          (!closestParent ||
            Object.keys(closestParent.metadata.breakdowns).length <
              Object.keys(potentialParent.metadata.breakdowns).length)
        ) {
          closestParent = potentialParent;
          isSelected = false;
        }
      });

      if (closestParent) {
        return isSelected;
      }
      return true;
    });

    return filtered.map((i) => i.key);
  }, [rows, selectionState.deselectedRowIds, selectionState.selectedRowIds]);

  return result;
}

export const flattenRows = (rows: PerformanceTrackingTableRow[]) => {
  const flattened: (Omit<TableRowType, "children"> | LoadingRowType)[] = [];

  rows.forEach((r) => {
    if (r.rowType === "loading") {
      return flattened.push(r);
    }
    flattened.push(omit(r, "children"));

    if (r.children?.length) {
      flattened.push(...flattenRows(r.children));
    }
    return;
  });

  return flattened;
};
